import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Download, Loader2, FileSignature, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function PatientReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    api.get(`/treatment-plans/${id}/patient-report`).then((r) => setRep(r.data)).catch((e) => setErr(e.response?.data?.detail || "Could not load the patient report"));
  }, [id]);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/treatment-plans/${id}/patient-report/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `CONTAHA_Patient_Report_${(rep?.data?.patient?.name || "patient").replace(/\s+/g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
    finally { setPdfBusy(false); }
  };

  const nav = (
    <div className="flex items-center gap-1 rounded-xl border border-[#E2E8F0] dark:border-[#223139] bg-white dark:bg-[#0E1A21] p-1" data-testid="report-tabs">
      <button onClick={() => navigate(`/treatment-plan/${id}`)} data-testid="tab-treatment-plan" className="rounded-lg px-3 py-1.5 text-xs font-semibold text-[#687173] dark:text-[#8B9698] hover:bg-[#F4FBF7] dark:hover:bg-white/5">Treatment Plan</button>
      <button data-testid="tab-patient-report" className="rounded-lg bg-[#00A85A] px-3 py-1.5 text-xs font-semibold text-white">Patient Report</button>
      <button onClick={() => navigate(`/treatment-plan/${id}#consent`)} data-testid="tab-consent" className="rounded-lg px-3 py-1.5 text-xs font-semibold text-[#687173] dark:text-[#8B9698] hover:bg-[#F4FBF7] dark:hover:bg-white/5 flex items-center gap-1"><FileSignature className="h-3 w-3" />Consent</button>
    </div>
  );

  if (err) {
    return (
      <div>
        <PageHeader breadcrumb={["Clinical", "Patient Report"]} title="Patient Report" actions={nav} />
        <div className="rounded-2xl border border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10 p-6 text-sm text-amber-800 dark:text-amber-300" data-testid="patient-report-locked">
          <ShieldCheck className="h-5 w-5 mb-2" />{err}
          <div className="mt-4"><Button variant="outline" onClick={() => navigate(`/treatment-plan/${id}`)} className="rounded-lg"><ArrowLeft className="h-4 w-4 mr-1.5" />Back to Treatment Plan</Button></div>
        </div>
      </div>
    );
  }
  if (!rep) return <Skeleton className="h-[600px] rounded-2xl" />;

  return (
    <div>
      <PageHeader breadcrumb={["Clinical", "Treatment Plan", "Patient Report"]} title={`${rep.data.patient.name} — Patient Report`}
        subtitle={`${rep.data.condition.name} · Doctor-approved · ${rep.data.report_version}`}
        actions={<div className="flex flex-wrap items-center gap-2">{nav}
          <Button onClick={downloadPdf} disabled={pdfBusy} data-testid="patient-report-download-pdf" className="bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">{pdfBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}Download PDF</Button>
        </div>}
      />
      <div className="-mx-2 rounded-2xl overflow-hidden" data-testid="patient-report-frame">
        <iframe title="Patient Report" srcDoc={rep.html} className="w-full border-0 bg-[#F3F6F8]" style={{ minHeight: "85vh", height: "2400px" }} data-testid="patient-report-iframe" />
      </div>
    </div>
  );
}
